import { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  GripVertical,
  Eye,
  EyeOff,
  Lock,
  Search,
  X,
  AlertCircle,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Column category type
export type ColumnCategory = 'Financial' | 'Resource' | 'Status' | 'Timeline' | 'Client' | 'Performance';

// Column definition interface
export interface ColumnDefinition {
  id: string;
  name: string;
  label?: string; // Display label (defaults to name if not provided)
  description: string;
  category: ColumnCategory;
  visible: boolean;
  locked?: boolean;
  order: number;
  align?: 'left' | 'right'; // Text alignment for the column
}

// Preset configuration interface
export interface PresetConfiguration {
  id: string;
  name: string;
  columns: Array<{ id: string; visible: boolean }>;
}

// Default column definitions
const DEFAULT_COLUMNS: ColumnDefinition[] = [
  {
    id: 'project-code',
    name: 'Project Code',
    description: 'Unique code identifier for the project',
    category: 'Status',
    visible: true,
    order: 0,
  },
  {
    id: 'project-name',
    name: 'Project Name',
    description: 'Name of the project',
    category: 'Status',
    visible: true,
    locked: true,
    order: 1,
  },
  // Financial
  {
    id: 'budget',
    name: 'Budget',
    description: 'Total project budget amount',
    category: 'Financial',
    visible: true,
    order: 2,
    align: 'right',
  },
  {
    id: 'actual-cost',
    name: 'Actual Cost',
    description: 'Total costs incurred to date',
    category: 'Financial',
    visible: true,
    order: 3,
    align: 'right',
  },
  {
    id: 'revenue',
    name: 'Revenue',
    description: 'Total revenue generated',
    category: 'Financial',
    visible: false,
    order: 4,
    align: 'right',
  },
  {
    id: 'profit-margin',
    name: 'Profit Margin',
    description: 'Percentage profit margin',
    category: 'Financial',
    visible: true,
    order: 5,
    align: 'right',
  },
  {
    id: 'billing-status',
    name: 'Billing Status',
    description: 'Current billing and invoice status',
    category: 'Financial',
    visible: false,
    order: 6,
  },
  {
    id: 'wip-amount',
    name: 'WIP Amount',
    description: 'Work in Progress amount (costs incurred + profit earned - amount billed)',
    category: 'Financial',
    visible: false,
    order: 7,
    align: 'right',
  },
  {
    id: 'gross-profit',
    name: 'Gross Profit',
    description: 'Revenue minus direct costs',
    category: 'Financial',
    visible: false,
    order: 8,
    align: 'right',
  },
  {
    id: 'forecast-at-completion',
    name: 'Forecast at Completion',
    description: 'Projected total cost at project completion',
    category: 'Financial',
    visible: false,
    order: 9,
    align: 'right',
  },
  // Resource
  {
    id: 'project-manager',
    name: 'Project Manager',
    description: 'Assigned project manager',
    category: 'Resource',
    visible: true,
    order: 10,
  },
  {
    id: 'team-members',
    name: 'Team Members',
    description: 'Number of team members assigned',
    category: 'Resource',
    visible: false,
    order: 11,
    align: 'right',
  },
  {
    id: 'allocated-hours',
    name: 'Allocated Hours',
    description: 'Total hours allocated to project',
    category: 'Resource',
    visible: false,
    order: 12,
    align: 'right',
  },
  {
    id: 'utilisation-rate',
    name: 'Utilisation Rate',
    description: 'Resource utilisation percentage',
    category: 'Resource',
    visible: false,
    order: 13,
    align: 'right',
  },
  {
    id: 'resource-conflicts',
    name: 'Resource Conflicts',
    description: 'Number of resources with allocation conflicts',
    category: 'Resource',
    visible: false,
    order: 14,
    align: 'right',
  },
  // Status
  {
    id: 'project-status',
    name: 'Project Status',
    description: 'Current project status',
    category: 'Status',
    visible: true,
    order: 15,
  },
  {
    id: 'health-indicator',
    name: 'Health Indicator',
    description: 'Project health status (RAG)',
    category: 'Status',
    visible: true,
    order: 16,
  },
  {
    id: 'project-type',
    name: 'Project Type',
    description: 'Type of project (Fixed Price, T&M, Retainer)',
    category: 'Status',
    visible: false,
    order: 17,
  },
  {
    id: 'phase',
    name: 'Phase/Stage',
    description: 'Current project phase or stage',
    category: 'Status',
    visible: false,
    order: 18,
  },
  {
    id: 'completion-percentage',
    name: 'Completion Percentage',
    description: 'Percentage of project completed',
    category: 'Status',
    visible: true,
    order: 19,
    align: 'right',
  },
  {
    id: 'last-updated',
    name: 'Last Updated',
    description: 'When the project was last modified',
    category: 'Status',
    visible: false,
    order: 20,
  },
  // Timeline
  {
    id: 'start-date',
    name: 'Start Date',
    description: 'Project start date',
    category: 'Timeline',
    visible: true,
    order: 21,
  },
  {
    id: 'end-date',
    name: 'End Date',
    description: 'Project end date',
    category: 'Timeline',
    visible: true,
    order: 22,
  },
  {
    id: 'duration',
    name: 'Duration',
    description: 'Total project duration',
    category: 'Timeline',
    visible: false,
    order: 23,
  },
  {
    id: 'days-remaining',
    name: 'Days Remaining',
    description: 'Days until project completion',
    category: 'Timeline',
    visible: false,
    order: 24,
    align: 'right',
  },
  {
    id: 'next-milestone',
    name: 'Next Milestone',
    description: 'Upcoming milestone name and due date',
    category: 'Timeline',
    visible: false,
    order: 25,
  },
  // Client
  {
    id: 'client-name',
    name: 'Client Name',
    description: 'Name of the client organisation',
    category: 'Client',
    visible: true,
    order: 26,
  },
  {
    id: 'client-contact',
    name: 'Client Contact',
    description: 'Primary client contact person',
    category: 'Client',
    visible: false,
    order: 27,
  },
  {
    id: 'industry',
    name: 'Industry',
    description: 'Client industry sector',
    category: 'Client',
    visible: false,
    order: 28,
  },
  {
    id: 'account-manager',
    name: 'Account Manager',
    description: 'Assigned account manager',
    category: 'Client',
    visible: false,
    order: 29,
  },
  // Performance
  {
    id: 'milestones-completed',
    name: 'Milestones Completed',
    description: 'Number of completed milestones',
    category: 'Performance',
    visible: false,
    order: 30,
  },
  {
    id: 'overdue-milestones',
    name: 'Overdue Milestones',
    description: 'Number of milestones past their due date',
    category: 'Performance',
    visible: false,
    order: 31,
  },
  {
    id: 'tasks-overdue',
    name: 'Overdue Tasks',
    description: 'Number of overdue tasks',
    category: 'Performance',
    visible: false,
    order: 32,
  },
  {
    id: 'risk-level',
    name: 'Risk Level',
    description: 'Overall project risk assessment',
    category: 'Performance',
    visible: false,
    order: 33,
  },
  {
    id: 'quality-score',
    name: 'Quality Score',
    description: 'Project quality rating',
    category: 'Performance',
    visible: false,
    order: 34,
  },
];

// Preset configurations
const PRESETS: PresetConfiguration[] = [
  {
    id: 'project-manager',
    name: 'Project Manager View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'client-name', visible: true },
      { id: 'project-status', visible: true },
      { id: 'health-indicator', visible: true },
      { id: 'completion-percentage', visible: true },
      { id: 'team-members', visible: true },
      { id: 'tasks-overdue', visible: true },
      { id: 'start-date', visible: true },
      { id: 'end-date', visible: true },
      { id: 'days-remaining', visible: true },
    ],
  },
  {
    id: 'finance',
    name: 'Finance View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'client-name', visible: true },
      { id: 'budget', visible: true },
      { id: 'actual-cost', visible: true },
      { id: 'revenue', visible: true },
      { id: 'profit-margin', visible: true },
      { id: 'billing-status', visible: true },
      { id: 'project-status', visible: true },
    ],
  },
  {
    id: 'executive',
    name: 'Executive Summary',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'client-name', visible: true },
      { id: 'project-manager', visible: true },
      { id: 'health-indicator', visible: true },
      { id: 'budget', visible: true },
      { id: 'profit-margin', visible: true },
      { id: 'completion-percentage', visible: true },
      { id: 'risk-level', visible: true },
    ],
  },
  {
    id: 'resource',
    name: 'Resource View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'project-manager', visible: true },
      { id: 'team-members', visible: true },
      { id: 'allocated-hours', visible: true },
      { id: 'utilisation-rate', visible: true },
      { id: 'start-date', visible: true },
      { id: 'end-date', visible: true },
      { id: 'project-status', visible: true },
    ],
  },
];

interface ColumnConfiguratorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDefinition[];
  onSave: (columns: ColumnDefinition[]) => void;
  presets?: PresetConfiguration[]; // Optional custom presets
  defaultColumns?: ColumnDefinition[]; // Optional default columns for reset
}

export function ColumnConfiguratorDrawer({
  open,
  onOpenChange,
  columns: initialColumns,
  onSave,
  presets = PRESETS,
  defaultColumns = DEFAULT_COLUMNS,
}: ColumnConfiguratorDrawerProps) {
  const [columns, setColumns] = useState<ColumnDefinition[]>(initialColumns);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ColumnCategory | 'all'>('all');
  const [selectedPreset, setSelectedPreset] = useState<string>('custom');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Reset state when drawer opens
  useEffect(() => {
    if (open) {
      setColumns(initialColumns);
      setSearchTerm('');
      setCategoryFilter('all');
      setSelectedPreset('custom');
      setHasChanges(false);
    }
  }, [open, initialColumns]);

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(columns) !== JSON.stringify(initialColumns);
    setHasChanges(changed);
    if (changed && selectedPreset !== 'custom') {
      setSelectedPreset('custom');
    }
  }, [columns, initialColumns, selectedPreset]);

  // Filter columns based on search and category
  const filteredColumns = useMemo(() => {
    return columns.filter((col) => {
      const matchesSearch =
        searchTerm === '' ||
        col.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        col.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || col.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [columns, searchTerm, categoryFilter]);

  // Count visible columns
  const visibleCount = columns.filter((col) => col.visible).length;
  const totalCount = columns.length;

  // Handle preset selection
  const handlePresetChange = (presetId: string) => {
    if (presetId === 'custom') {
      setSelectedPreset('custom');
      return;
    }

    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;

    const updatedColumns = columns.map((col) => {
      const presetCol = preset.columns.find((pc) => pc.id === col.id);
      return {
        ...col,
        visible: presetCol ? presetCol.visible : false,
      };
    });

    setColumns(updatedColumns);
    setSelectedPreset(presetId);
  };

  // Handle visibility toggle
  const handleToggleVisibility = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId && !col.locked ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // Handle show all
  const handleShowAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  // Handle hide all
  const handleHideAll = () => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        visible: col.locked ? true : false,
      }))
    );
  };

  // Handle reset to default
  const handleResetToDefault = () => {
    setColumns(defaultColumns);
    setSelectedPreset('custom');
  };

  // Handle drag start
  const handleDragStart = (index: number) => {
    const column = filteredColumns[index];
    if (column.locked) return;
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const column = filteredColumns[index];
    if (column.locked) return;
    setDragOverIndex(index);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const draggedColumn = filteredColumns[draggedIndex];
    const targetColumn = filteredColumns[dropIndex];

    if (draggedColumn.locked || targetColumn.locked) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder in the full columns array
    const newColumns = [...columns];
    const draggedOriginalIndex = newColumns.findIndex((c) => c.id === draggedColumn.id);
    const targetOriginalIndex = newColumns.findIndex((c) => c.id === targetColumn.id);

    newColumns.splice(draggedOriginalIndex, 1);
    newColumns.splice(targetOriginalIndex, 0, draggedColumn);

    // Update order property
    const reorderedColumns = newColumns.map((col, idx) => ({ ...col, order: idx }));

    setColumns(reorderedColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle save
  const handleSave = async () => {
    // Validate at least one column is visible (besides locked ones)
    const visibleNonLockedColumns = columns.filter((col) => col.visible && !col.locked);
    if (visibleNonLockedColumns.length === 0) {
      toast.error('At least one column must be visible in addition to Project Name', {
        description: 'Please select at least one column to display.',
        icon: <AlertCircle className="h-4 w-4" />,
      });
      return;
    }

    setIsSaving(true);
    try {
      // Simulate save delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      onSave(columns);
      toast.success('Column configuration saved', {
        description: 'Your column preferences have been updated.',
        icon: <Check className="h-4 w-4" />,
      });
      onOpenChange(false);
    } catch {
      toast.error('Failed to save configuration', {
        description: 'Please try again or contact support if the problem persists.',
        action: {
          label: 'Retry',
          onClick: () => handleSave(),
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      onOpenChange(false);
    }
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent 
          className="w-full sm:max-w-[600px] flex flex-col p-0"
          onInteractOutside={(e) => {
            if (hasChanges) {
              e.preventDefault();
              setShowUnsavedDialog(true);
            }
          }}
          onEscapeKeyDown={(e) => {
            if (hasChanges) {
              e.preventDefault();
              setShowUnsavedDialog(true);
            }
          }}
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <SheetTitle>Configure Columns</SheetTitle>
            <SheetDescription>
              Customize which columns appear in the project list and control their display order.
            </SheetDescription>
          </SheetHeader>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Preset Selector */}
            <div className="px-6 pt-4 pb-3 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <label htmlFor="preset-select" className="text-sm font-medium whitespace-nowrap">
                  Preset:
                </label>
                <Select value={selectedPreset} onValueChange={handlePresetChange}>
                  <SelectTrigger id="preset-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {presets.map((preset) => (
                      <SelectItem key={preset.id} value={preset.id}>
                        {preset.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Filter Controls */}
            <div className="px-6 py-4 space-y-3 border-b">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search columns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Select
                  value={categoryFilter}
                  onValueChange={(value) => setCategoryFilter(value as ColumnCategory | 'all')}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Financial">Financial</SelectItem>
                    <SelectItem value="Resource">Resource</SelectItem>
                    <SelectItem value="Status">Status</SelectItem>
                    <SelectItem value="Timeline">Timeline</SelectItem>
                    <SelectItem value="Client">Client</SelectItem>
                    <SelectItem value="Performance">Performance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Actions */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowAll}
                    className="h-8"
                  >
                    Show All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleHideAll}
                    className="h-8"
                  >
                    Hide All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetToDefault}
                    className="h-8"
                  >
                    Reset to Default
                  </Button>
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {filteredColumns.length} of {totalCount} columns
                  {visibleCount > 0 && (
                    <span className="ml-2">
                      ({visibleCount} visible)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Column List */}
            <div className="flex-1 overflow-hidden min-h-0">
              <ScrollArea className="h-full">
                <ScrollAreaViewport className="h-full">
                  <div className="px-6 py-4 space-y-1">
                  {filteredColumns.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No columns found</p>
                      <p className="text-xs mt-1">Try adjusting your search or filter</p>
                    </div>
                  ) : (
                    filteredColumns.map((column, index) => (
                      <div
                        key={column.id}
                        draggable={!column.locked}
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={cn(
                          'group flex items-center gap-3 p-3 rounded-lg border bg-card transition-all',
                          'hover:bg-accent/50 hover:border-accent',
                          column.locked && 'bg-muted/50',
                          draggedIndex === index && 'opacity-50',
                          dragOverIndex === index && 'border-primary border-2'
                        )}
                      >
                        {/* Drag Handle or Lock Icon */}
                        <div className="flex-shrink-0">
                          {column.locked ? (
                            <Lock className="h-4 w-4 text-muted-foreground" aria-label="Locked column" />
                          ) : (
                            <GripVertical
                              className={cn(
                                'h-5 w-5 text-muted-foreground cursor-grab active:cursor-grabbing',
                                'group-hover:text-foreground transition-colors'
                              )}
                              aria-label="Drag to reorder"
                            />
                          )}
                        </div>

                        {/* Column Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-sm truncate">{column.name}</h4>
                            <Badge variant="secondary" className="text-xs">
                              {column.category}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {column.description}
                          </p>
                        </div>

                        {/* Visibility Toggle */}
                        <button
                          onClick={() => handleToggleVisibility(column.id)}
                          disabled={column.locked}
                          className={cn(
                            'flex-shrink-0 p-2 rounded-md transition-colors',
                            column.locked
                              ? 'cursor-not-allowed opacity-50'
                              : 'hover:bg-accent cursor-pointer',
                            column.visible ? 'text-primary' : 'text-muted-foreground'
                          )}
                          aria-label={column.visible ? 'Hide column' : 'Show column'}
                        >
                          {column.visible ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))
                  )}
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>
          </div>

          {/* Footer */}
          <SheetFooter className="px-6 py-4 border-t bg-muted/30">
            <div className="flex items-center justify-between w-full">
              <div className="text-xs text-muted-foreground">
                {hasChanges && (
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-burntorange-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardChanges} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Export default columns for use in other components
export { DEFAULT_COLUMNS, PRESETS };
